import React, { useMemo, useState, useContext, useRef } from "react";
import {
  Grid,
  MenuItem,
  useTheme,
  Typography,
  Menu,
  Box,
  Avatar,
} from "@mui/material";
import { selectAllCompanies } from "../../features";
import { AppContext } from "../../contextProviders/AppContext";
import { translation } from "../../components/Dictionary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaceIcon from "@mui/icons-material/Place";
const FIXED_GROUPS = window.URENT_APP_GROUPS;
const FEATURE_SELECT_CUSTOM_LABEL = FIXED_GROUPS?.includes("rsteam")
  ? true
  : false;

export default function ProviderSelector({
  inputClassName,
  fullWidth,
  providerSelectOpen,
  setProviderSelectOpen,
  contentHeigth,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    companies,
    handleSelectedCompany,
    selectedCompany,
    language,
    mobileViewPort,
    setOffers,
    enableCustomProviderLabel,
    FEATURE_NO_TIME_SELECT,
  } = useContext(AppContext);

  const FEATURE_HIDE_SELECTED_COMPANY_TITLE = useMemo(() => {
    if (
      selectedCompany?.group === "gnista" ||
      selectedCompany?.group === "huuma"
    ) {
      return true;
    }
  }, [selectedCompany]);

  const provideSelectorLabel = useMemo(() => {
    if (enableCustomProviderLabel()) {
      return enableCustomProviderLabel();
    }
    return translation.get(language, "Provider default title");
  }, [language]);

  const [companyId, setCompanyId] = useState(selectedCompany?.id ?? "none");

  const [dirty, setDirty] = useState(false);

  // Be careful with this setting, as it will not behave correctly if all Companys
  // within the group use the same settings
  const FEATURE_SELECT_ALL_COMPANIES = selectAllCompanies(companies);
  //
  const orderedCompanies = useMemo(() => {
    //Only sorts if both of our c1 & c2 orderNumbers are null or numbers (null - null = 0, so the order doesn't change)
    return [...companies].sort((c1, c2) => {
      return (
        (c1.orderNumber === null) - (c2.orderNumber === null) ||
        c1.orderNumber - c2.orderNumber
      );
    });
  }, [companies]);

  const chooseCompanyLabel = useMemo(() => {
    if (enableCustomProviderLabel())
      return translation.get(language, "Select product group");
    return translation.get(language, "Select company label");
  }, [language]);

  const [selected, setSelected] = useState();
  const handleChangeCompany = (id) => {
    setCompanyId(id);
    if (id === "all") {
      // Set company with  suitable settings, and id "all"
      handleSelectedCompany(FEATURE_SELECT_ALL_COMPANIES);
    } else {
      handleSelectedCompany(companies.find((c) => c.id === id));
    }
  };

  const openMenu = (e) => {
    setProviderSelectOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setProviderSelectOpen(false);
  };
  const handleClickMenuItem = (e) => {
    handleSelectedCompany(e);
    setOffers([]);
    setAnchorEl(null);
    setProviderSelectOpen(false);
  };

  if (!mobileViewPort) {
    return (
      <Box
        sx={{
          width: FEATURE_NO_TIME_SELECT ? "35%" : "25%",
          height: contentHeigth,
          paddingLeft: mobileViewPort ? "15px" : "3px",
          boxShadow: providerSelectOpen ? theme.shadows[2] : "transparent",
          /*   bgcolor: mobileViewPort
          ? theme.palette.primary.dialogBackground
          : undefined, */
          border: mobileViewPort ? "1px solid lightgrey" : undefined,
          borderRadius: mobileViewPort ? "15px" : undefined,
          cursor: "pointer",
          borderTopLeftRadius: "45px",
          borderBottomLeftRadius: " 45px",
          borderTopRightRadius: "10px",
          borderBottomRightRadius: "10px",
          transition: "250ms ease-in-out",
          backgroundColor: Boolean(anchorEl) ? "white" : "transparent",
          "&:hover": {
            bgcolor: "rgba(0,0,0,0.05)",
            boxShadow: theme.shadows[2],
          },
          marginLeft: "2px",
          marginRight: "10px",
        }}
      >
        <Box
          sx={{
            marginLeft: "2px",
            marginRight: "10px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            height: contentHeigth,
            borderTopLeftRadius: "45px",
            borderBottomLeftRadius: "45px",
          }}
          onClick={(e) => openMenu(e)}
        >
          <Box sx={{ marginLeft: "20px" }}>
            {selectedCompany && (
              <Box>
                {!FEATURE_HIDE_SELECTED_COMPANY_TITLE && (
                  <Typography
                    sx={{
                      color: theme.palette.primary.mainText,
                      fontSize: "16px",
                      fontFamily: "Sofia Pro",
                      fontWeight: 500,
                    }}
                  >
                    {translation.get(language, "Selected company")}
                  </Typography>
                )}

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.mainText,
                  }}
                >
                  {selectedCompany.displayName}
                </Typography>
              </Box>
            )}
            {!selectedCompany && (
              <Box>
                <Typography
                  sx={{
                    color: theme.palette.primary.mainText,
                    fontSize: "16px",
                    fontFamily: "Sofia Pro",
                  }}
                >
                  {provideSelectorLabel}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.primary.mainText,
                  }}
                >
                  {chooseCompanyLabel}
                </Typography>
              </Box>
            )}
          </Box>

          <ExpandMoreIcon sx={{ color: "lightgrey" }} />
        </Box>
        <Menu
          id="providers-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          keepMounted
          onClose={() => closeMenu()}
          PaperProps={{
            style: {
              borderRadius: "15px",
              padding: "5px",
              maxHeight: "350px",
              overflow: "auto",
            },
          }}
        >
          {orderedCompanies.map((company) => (
            <MenuItem
              sx={{ padding: "15px", borderRadius: "15px" }}
              key={company.id}
              onClick={() => handleClickMenuItem(company)}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    marginRight: "10px",
                    bgcolor: "rgba(0,0,0,0.10)",
                  }}
                >
                  <PlaceIcon sx={{ color: "rgba(0,0,0,0.65)" }} />
                </Avatar>

                <Typography variant="body1" sx={{ fontSize: "16px" }}>
                  {company.displayName ?? company.name}
                </Typography>
              </Box>
            </MenuItem>
          ))}
          {FEATURE_SELECT_ALL_COMPANIES && (
            <MenuItem value="all">Kaikki 24/7 toimipisteet</MenuItem>
          )}
        </Menu>
      </Box>
    );
  }
  if (mobileViewPort) {
    return (
      <Box
        sx={{
          width: "100%",
          margin: "10px",
          boxShadow: providerSelectOpen ? theme.shadows[2] : "transparent",
          /*   bgcolor: mobileViewPort
          ? theme.palette.primary.dialogBackground
          : undefined, */
          border: mobileViewPort ? "1px solid lightgrey" : undefined,
          borderRadius: mobileViewPort ? "15px" : undefined,
          cursor: "pointer",
          borderRadius: "14px",
          transition: "250ms ease-in-out",
          backgroundColor: Boolean(anchorEl) ? "white" : "transparent",
          "&:hover": {
            bgcolor: "rgba(0,0,0,0.05)",
          },
        }}
      >
        <Box
          sx={{
            marginLeft: "2px",
            marginRight: "10px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            height: contentHeigth,
            borderTopLeftRadius: "45px",
            borderBottomLeftRadius: "45px",
          }}
          onClick={(e) => openMenu(e)}
        >
          <Box sx={{ marginLeft: "2px", marginLeft: "20px" }}>
            {selectedCompany && (
              <Box>
                <Typography
                  sx={{
                    color: theme.palette.primary.mainText,
                    fontSize: "16px",
                    fontFamily: "Sofia Pro",
                    fontWeight: 500,
                  }}
                >
                  {translation.get(language, "Selected company")}
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {selectedCompany.displayName}
                </Typography>
              </Box>
            )}
            {!selectedCompany && (
              <Box>
                <Typography
                  sx={{
                    color: theme.palette.primary.mainText,
                    fontSize: "16px",
                    fontFamily: "Sofia Pro",
                  }}
                >
                  {provideSelectorLabel}
                </Typography>

                <Typography variant="body1"> {chooseCompanyLabel}</Typography>
              </Box>
            )}
          </Box>

          <ExpandMoreIcon sx={{ color: "lightgrey" }} />
        </Box>
        <Menu
          id="providers-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          keepMounted
          onClose={() => closeMenu()}
          PaperProps={{
            style: {
              borderRadius: "15px",
              padding: "15px",
              width: "80vw",
              maxHeight: "50vh",
            },
          }}
        >
          {companies.map((company) => (
            <MenuItem
              sx={{ padding: "15px", borderRadius: "15px" }}
              key={company.id}
              onClick={() => handleClickMenuItem(company)}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    marginRight: "10px",
                    bgcolor: "rgba(0,0,0,0.10)",
                  }}
                >
                  <PlaceIcon sx={{ color: "rgba(0,0,0,0.65)" }} />
                </Avatar>

                <Typography variant="body1" sx={{ fontSize: "16px" }}>
                  {company.displayName ?? company.name}
                </Typography>
              </Box>
            </MenuItem>
          ))}
          {FEATURE_SELECT_ALL_COMPANIES && (
            <MenuItem value="all">Kaikki 24/7 toimipisteet</MenuItem>
          )}
        </Menu>
      </Box>
    );
  }
}
